// In-page notifications (the prototype's toasts). No browser dialogs anywhere in the console.
// The user, 2026-10-28: pop-ups only for an action that didn't go through (a talk-down or push-to-talk not played,
// nobody reached, a failed action, a claim / take / resume refused, an agent answer that didn't land); everything else
// shows where it happens (tabs, queue, timeline, Agent tab). Each stays until dismissed. One stack for the whole app
// (ToastProvider in the shell), bottom-left, in one column with the agent's floating requests (copilot.tsx portals them
// into the slot above the toasts), so nothing floats over the action rail or a drawer's buttons.

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { AuthImg } from '../components/AuthMedia.tsx';
import { Icon, type IconName } from '../components/Icon.tsx';

export interface ToastSpec {
  title: string;
  body?: string;
  icon?: IconName;
  /** 'ai': something the AI agent said or saw (violet). */
  tone?: 'ok' | 'warn' | 'bad' | 'raise' | 'ai';
  /** Phase 4: an authenticated image URL shown under the body (the watch-hit frame). */
  media?: string | null;
  actions?: { label: string; onClick: () => void; primary?: boolean }[];
  /** How long it shows; by default until dismissed. */
  ms?: number;
}
/** How long a toast shows, in ms; null: until dismissed (every pop-up is a failure, so none is missed while the operator looks away). */
export function toastTtl(t: Pick<ToastSpec, 'tone' | 'ms'>): number | null {
  return t.ms ?? null;
}

interface ToastItem extends ToastSpec {
  id: number;
}

export function useToasts(): { toasts: ToastItem[]; toast: (t: ToastSpec) => void; dismiss: (id: number) => void } {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());
  const dismiss = useCallback((id: number) => {
    setToasts((l) => l.filter((t) => t.id !== id));
    const tm = timers.current.get(id);
    if (tm) clearTimeout(tm);
    timers.current.delete(id);
  }, []);
  const toast = useCallback(
    (t: ToastSpec) => {
      const id = ++seq.current;
      setToasts((l) => [...l.slice(-3), { ...t, id }]);
      const ttl = toastTtl(t);
      if (ttl !== null) timers.current.set(id, setTimeout(() => dismiss(id), ttl));
    },
    [dismiss],
  );
  useEffect(() => {
    const m = timers.current;
    return () => m.forEach((tm) => clearTimeout(tm));
  }, []);
  return { toasts, toast, dismiss };
}

export function Toasts({ toasts, dismiss }: { toasts: ToastItem[]; dismiss: (id: number) => void }) {
  return (
    <div className="toasts" aria-live="polite" role="status">
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.tone ?? ''}`}>
          <Icon name={t.icon ?? (t.tone === 'bad' || t.tone === 'warn' ? 'alert' : 'check')} />
          <div>
            <b>{t.title}</b>
            {t.body && <p>{t.body}</p>}
            {t.media && (
              <span className="tmedia">
                <AuthImg url={t.media} alt="Frame from the camera" fallback="" />
              </span>
            )}
            {t.actions && (
              <div className="ta">
                {t.actions.map((a) => (
                  <button
                    key={a.label}
                    type="button"
                    className={`btn ${a.primary ? 'pri' : ''}`}
                    onClick={() => {
                      a.onClick();
                      dismiss(t.id);
                    }}
                  >
                    {a.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button type="button" className="btn icon tx" onClick={() => dismiss(t.id)} aria-label="Dismiss notification">
            <Icon name="x" />
          </button>
        </div>
      ))}
    </div>
  );
}

type Toaster = (t: ToastSpec) => void;
const ToastContext = createContext<Toaster | null>(null);
const SlotContext = createContext<HTMLElement | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const { toasts, toast, dismiss } = useToasts();
  const [slot, setSlot] = useState<HTMLElement | null>(null);
  return (
    <ToastContext.Provider value={toast}>
      <SlotContext.Provider value={slot}>
        {children}
        <div className="notices">
          <div ref={setSlot} className="notice-slot" />
          <Toasts toasts={toasts} dismiss={dismiss} />
        </div>
      </SlotContext.Provider>
    </ToastContext.Provider>
  );
}

/** The element above the toasts where the agent's floating requests go (null until it mounts). */
export function useNoticeSlot(): HTMLElement | null {
  return useContext(SlotContext);
}

/** The app-wide toast function (needs ToastProvider). */
export function useToaster(): Toaster {
  const t = useContext(ToastContext);
  if (!t) throw new Error("ToastProvider missing");
  return t;
}
